import { useEffect, useRef, type RefObject } from 'react';
import { Popover, type PopoverPlacement } from '@sorrem/ui';
import styles from './ContextMenu.module.scss';

/** One choice in a `ContextMenu`: grayed out with its reason when `disabledReason` is set. */
export interface ContextMenuItem {
    readonly label: string;
    readonly disabledReason?: string;
    readonly onSelect: () => void;
}

interface ContextMenuProps {
    readonly open: boolean;
    /** What the menu opens beside: the thing right-clicked. */
    readonly anchorRef: RefObject<HTMLElement>;
    readonly items: readonly ContextMenuItem[];
    /** Called on a pick, on Esc, or on a click outside the menu. */
    readonly onClose: () => void;
    /** The menu's accessible name, e.g. the item's. */
    readonly ariaLabel: string;
    readonly placement?: PopoverPlacement;
}

/**
 * A right-click menu beside what was clicked (#267): a list of choices,
 * each closing the menu as it is picked. A choice that can't be made now
 * stays in the list, grayed out, with the reason under it. The first choice
 * that can be made takes focus, so the keyboard can pick it.
 */
export function ContextMenu({ open, anchorRef, items, onClose, ariaLabel, placement = 'right' }: ContextMenuProps): JSX.Element {
    const menuRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (open) {
            menuRef.current?.querySelector<HTMLButtonElement>('button:not(:disabled)')?.focus();
        }
    }, [open]);

    return (
        <Popover open={open} anchorRef={anchorRef} placement={placement} onClose={onClose} className={styles.contextMenuPopover}>
            <div ref={menuRef} className={styles.contextMenu} role='menu' aria-label={ariaLabel}>
                {items.map((item) => (
                    <button
                        key={item.label}
                        type='button'
                        role='menuitem'
                        className={styles.item}
                        disabled={item.disabledReason !== undefined}
                        onClick={() => {
                            onClose();
                            item.onSelect();
                        }}
                    >
                        {item.label}
                        {item.disabledReason !== undefined && <span className={styles.reason}>{item.disabledReason}</span>}
                    </button>
                ))}
            </div>
        </Popover>
    );
}
